import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import {
  fetchTermsAndPolicies,
  fetchStaticChoice,
  submitUserFeedback,
  fetchVideoCategories,
  fetchVideoSeries,
  fetchOfficialVideos,
  searchOfficialVideos,
  submitPrayerRequest,
  fetchPublicPrayers,
  fetchTotalPrayerCount,
} from '../../services/main/mainApi';
import { MainState, FeedbackFormData, PrayerFormData, OfficialVideo, VideoSearchParams, VideoCategory, VideoSeries } from '../../types/main/mainTypes';

const initialState: MainState = {
  termsAndPolicies: [],
  staticChoices: {},

  videoCategories: [],
  videoSeries: [],
  officialVideos: [],

  loading: {
    fetchTerms: false,
    fetchChoices: false,
    submitFeedback: false,
    fetchVideoCategories: false,
    fetchVideoSeries: false,
    fetchOfficialVideos: false,

    submitPrayer: false,
    fetchPrayers: false,
  },

  publicPrayers: [],
  prayerCount: 0,
  prayerPage: 1,
  totalPrayerCount: 0,

  error: null,
  status: null,
  successMessage: null,

  searchResults: [] as OfficialVideo[],
  searchLoading: false,
  searchError: null as string | null,

};



interface ErrorPayload {
  error?: string;
  detail?: string;
}

// ----------------------------- Thunks -----------------------------

// helpers: build a stable cache key from name + params
export const buildStaticChoiceKey = (
  name: string,
  params?: Record<string, any> | undefined
) => {
  if (!params || Object.keys(params).length === 0) return name;
  const qs = Object.entries(params)
    .filter(([, v]) => v !== undefined && v !== null && v !== "")
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([k, v]) => `${encodeURIComponent(k)}=${encodeURIComponent(String(v))}`)
    .join("&");
  return `${name}?${qs}`;
};

// --- UPDATED: supports string OR { name, params }
export const getStaticChoice = createAsyncThunk(
  "main/getStaticChoice",
  async (
    arg: string | { name: string; params?: Record<string, any> },
    { rejectWithValue }
  ) => {
    try {
      const name = typeof arg === "string" ? arg : arg.name;
      const params = typeof arg === "string" ? undefined : arg.params;
      // NOTE: fetchStaticChoice now accepts optional params
      const data = await fetchStaticChoice(name, params);
      return { name, params, data };
    } catch (error: any) {
      return rejectWithValue(error?.response?.data || "Failed to fetch choices");
    }
  }
);

// -----------------------------------------------------------------
export const getTermsAndPolicies = createAsyncThunk(
  'main/getTermsAndPolicies',
  async (_, { rejectWithValue }) => {
    try {
      return await fetchTermsAndPolicies();
    } catch (error: any) {
      return rejectWithValue(error.response?.data || 'Failed to fetch policies');
    }
  }
);

// -----------------------------------------------------------------
export const submitUserFeedbackThunk = createAsyncThunk(
  'main/submitUserFeedback',
  async (feedbackData: FeedbackFormData, { rejectWithValue }) => {
    try {
      return await submitUserFeedback(feedbackData);
    } catch (error: any) {
      return rejectWithValue(error.response?.data || 'Failed to submit feedback');
    }
  }
);

// ------------------------------------------------------------------------------
export const submitPrayer = createAsyncThunk(
  'main/submitPrayer',
  async (data: PrayerFormData, { rejectWithValue }) => {
    try {
      return await submitPrayerRequest(data);
    } catch (error: any) {
      return rejectWithValue(error.response?.data || 'Failed to submit prayer');
    }
  }
);

export const getPublicPrayers = createAsyncThunk(
  'main/getPublicPrayers',
  async (page: number, { rejectWithValue }) => {
    try {
      return await fetchPublicPrayers(page);
    } catch (error: any) {
      return rejectWithValue(error.response?.data || 'Failed to fetch prayers');
    }
  }
);

export const getTotalPrayerCount = createAsyncThunk(
  'main/getTotalPrayerCount',
  async (_, { rejectWithValue }) => {
    try {
      return await fetchTotalPrayerCount();
    } catch (error: any) {
      return rejectWithValue(error.response?.data || 'Failed to fetch total prayer count');
    }
  }
);


// ------------------------------------------------------------------------------
export const getVideoCategories = createAsyncThunk<
  VideoCategory[], void, { rejectValue: string }
>(
  'main/getVideoCategories',
  async (_, { rejectWithValue }) => {
    try {
      return await fetchVideoCategories(); // ← همیشه آرایه
    } catch (err: any) {
      return rejectWithValue(err?.response?.data || 'Failed to fetch video categories');
    }
  }
);

export const getVideoSeries = createAsyncThunk<
  VideoSeries[], void, { rejectValue: string }
>(
  'main/getVideoSeries',
  async (_, { rejectWithValue }) => {
    try {
      return await fetchVideoSeries(); // ← همیشه آرایه
    } catch (err: any) {
      return rejectWithValue(err?.response?.data || 'Failed to fetch video series');
    }
  }
);

export const getOfficialVideos = createAsyncThunk<
  OfficialVideo[], void, { rejectValue: string }
>(
  'main/getOfficialVideos',
  async (_, { rejectWithValue }) => {
    try {
      return await fetchOfficialVideos(); // ← همیشه آرایه
    } catch (err: any) {
      return rejectWithValue(err?.response?.data || 'Failed to fetch official videos');
    }
  }
);


export const searchOfficialVideosThunk = createAsyncThunk(
  'main/searchOfficialVideos',
  async (params: VideoSearchParams, { rejectWithValue }) => {
    try {
      return await searchOfficialVideos(params);
    } catch (error: any) {
      return rejectWithValue(error.response?.data || 'Search failed');
    }
  }
);

// ----------------------------- Slice -----------------------------

const mainSlice = createSlice({
  name: 'main',
  initialState,
  reducers: {
    clearMessages(state) {
      state.error = null;
      state.successMessage = null;
    },
    setPrayerPage(state, action) {
      state.prayerPage = action.payload;
    }
  },
  extraReducers: (builder) => {
    builder

      // --- fetchTermsAndPolicies ---
      .addCase(getTermsAndPolicies.pending, (state) => {
        state.loading.fetchTerms = true;
        state.error = null;
        state.successMessage = null;
      })
      .addCase(getTermsAndPolicies.fulfilled, (state, action) => {
        state.loading.fetchTerms = false;
        const p = action.payload as unknown;
        state.termsAndPolicies = Array.isArray(p) ? p
          : Array.isArray((p as any)?.results) ? (p as any).results
            : [];
      })
      .addCase(getTermsAndPolicies.rejected, (state, action) => {
        state.loading.fetchTerms = false;
        state.error = action.payload as string;
      })

      // --- getStaticChoice ---
      .addCase(getStaticChoice.pending, (state, action) => {
        state.loading.fetchChoices = true;
        state.error = null;
      })
      .addCase(getStaticChoice.fulfilled, (state, action) => {
        const { name, params, data } = action.payload as {
          name: string;
          params?: Record<string, any>;
          data: [string, string][];
        };

        // store under composite key (name + params) for caching variants
        const compositeKey = buildStaticChoiceKey(name, params);
        state.staticChoices[compositeKey] = data;

        // backward-compat: if no params, also store under plain name
        if (!params) {
          state.staticChoices[name] = data;
        }

        state.loading.fetchChoices = false;
      })
      .addCase(getStaticChoice.rejected, (state, action) => {
        state.loading.fetchChoices = false;
        state.error = action.payload as string;
      })


      // --- submitUserFeedback ---
      .addCase(submitUserFeedbackThunk.pending, (state) => {
        state.loading.submitFeedback = true;
        state.error = null;
        state.successMessage = null;
      })
      .addCase(submitUserFeedbackThunk.fulfilled, (state, action) => {
        state.loading.submitFeedback = false;
        state.successMessage = action.payload?.message || 'Feedback submitted successfully.';
      })
      .addCase(submitUserFeedbackThunk.rejected, (state, action) => {
        state.loading.submitFeedback = false;

        const payload = action.payload as { error?: string; detail?: string } | string;
        state.error =
          typeof payload === 'string'
            ? payload
            : payload.error || payload.detail || 'Failed to submit feedback.';
      })

      // --- submitPrayer ---
      .addCase(submitPrayer.pending, (state) => {
        state.loading.submitPrayer = true;
        state.error = null;
        state.successMessage = null;
      })
      .addCase(submitPrayer.fulfilled, (state, action) => {
        state.loading.submitPrayer = false;
        state.successMessage = action.payload?.message || 'Prayer submitted successfully.';
      })
      .addCase(submitPrayer.rejected, (state, action) => {
        state.loading.submitPrayer = false;
        state.error = typeof action.payload === 'string' ? action.payload : 'Failed to submit prayer.';
      })

      // --- getPublicPrayers ---
      .addCase(getPublicPrayers.pending, (state) => {
        state.loading.fetchPrayers = true;
      })
      .addCase(getPublicPrayers.fulfilled, (state, action) => {
        state.loading.fetchPrayers = false;
        const { results, count } = action.payload;
        if (state.prayerPage === 1) {
          state.publicPrayers = results;
        } else {
          state.publicPrayers = [...state.publicPrayers, ...results];
        }
        state.prayerCount = count;
      })
      .addCase(getPublicPrayers.rejected, (state, action) => {
        state.loading.fetchPrayers = false;
        state.error = action.payload as string;
      })

      // --- getTotalPrayerCount ---
      .addCase(getTotalPrayerCount.fulfilled, (state, action) => {
        state.totalPrayerCount = action.payload;
      })


      // --- getVideoCategories ---
      .addCase(getVideoCategories.pending, (state) => {
        state.loading.fetchVideoCategories = true;
      })
      .addCase(getVideoCategories.fulfilled, (state, { payload }) => {
        state.videoCategories = Array.isArray(payload) ? payload : [];
        state.loading.fetchVideoCategories = false;
      })
      .addCase(getVideoCategories.rejected, (state, action) => {
        state.loading.fetchVideoCategories = false;
        state.error = action.payload as string;
      })

      // --- getVideoSeries ---
      .addCase(getVideoSeries.pending, (state) => {
        state.loading.fetchVideoSeries = true;
      })
      .addCase(getVideoSeries.fulfilled, (state, { payload }) => {
        state.videoSeries = Array.isArray(payload) ? payload : [];
        state.loading.fetchVideoSeries = false;
      })
      .addCase(getVideoSeries.rejected, (state, action) => {
        state.loading.fetchVideoSeries = false;
        state.error = action.payload as string;
      })

      // --- getOfficialVideos ---
      .addCase(getOfficialVideos.pending, (state) => {
        state.loading.fetchOfficialVideos = true;
      })
      .addCase(getOfficialVideos.fulfilled, (state, { payload }) => {
        state.officialVideos = Array.isArray(payload) ? payload : [];
        state.loading.fetchOfficialVideos = false;
      })
      .addCase(getOfficialVideos.rejected, (state, action) => {
        state.loading.fetchOfficialVideos = false;
        state.error = action.payload as string;
      })

      // --- searchOfficialVideosThunk ---
      .addCase(searchOfficialVideosThunk.pending, (state) => {
        state.searchLoading = true;
        state.searchError = null;
        state.searchResults = [];
      })
      .addCase(searchOfficialVideosThunk.fulfilled, (state, action) => {
        state.searchLoading = false;
        state.searchResults = action.payload;
      })
      .addCase(searchOfficialVideosThunk.rejected, (state, action) => {
        state.searchLoading = false;
        state.searchError = typeof action.payload === 'string'
          ? action.payload
          : 'Search failed.';
      })



  },
});

export const { clearMessages, setPrayerPage } = mainSlice.actions;
export default mainSlice.reducer;
